import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Reveal from "@/components/Reveal";
import Label from "@/components/Label";
import FaqItem from "@/components/FaqItem";
import Footer from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import { expertise, pricing, process, faq } from "@/content/translations";

export default function ExpertisePage() {
    const { lang } = useLanguage();
    const t = expertise[lang];
    const p = pricing[lang];
    const proc = process[lang];
    const f = faq[lang];

    return (
        <>
            <Header />
            <main data-testid="expertise-page" className="tm-page-in">
                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 pt-24 sm:pt-32">
                    <Reveal>
                        <Label>{t.label}</Label>
                        <h1 className="tm-serif text-[3.25rem] sm:text-[5rem] leading-[0.95] mt-6 text-[hsl(var(--tm-anthracite))]">
                            {t.h1a} <span className="italic text-[hsl(var(--tm-gold))]">{t.h1italic}</span>{t.h1b}
                        </h1>
                        <div className="mt-8 h-px w-28 bg-[hsl(var(--tm-gold))] tm-line-draw"></div>
                        <p className="mt-10 tm-serif text-[1.35rem] leading-relaxed text-[hsl(var(--tm-anthracite-soft))] max-w-2xl">
                            {t.intro}
                        </p>
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-28 sm:mt-36">
                    <Reveal>
                        <Label>{t.audiencesLabel}</Label>
                        <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.audiencesTitle}</h2>
                    </Reveal>
                    <Reveal stagger className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {t.audiences.map((a, i) => (
                            <div key={a.title} className="tm-card group p-8 transition-all duration-500 hover:border-[hsl(var(--tm-gold))]">
                                <div className="flex items-baseline justify-between">
                                    <h3 className="tm-serif text-xl text-[hsl(var(--tm-anthracite))]">{a.title}</h3>
                                    <span className="tm-mono tm-label text-[hsl(var(--tm-gold))] opacity-40 group-hover:opacity-100 transition-opacity duration-500">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="mt-4 h-px w-10 bg-[hsl(var(--tm-gold))] transition-all duration-500 group-hover:w-20"></div>
                                <p className="mt-5 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{a.text}</p>
                            </div>
                        ))}
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <Reveal>
                        <Label>{t.methodLabel}</Label>
                        <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.methodTitle}</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-[hsl(var(--tm-anthracite-soft))] leading-relaxed max-w-2xl">{t.methodText}</p>
                        </div>
                        <Reveal className="lg:col-span-5" delay={0.1}>
                            <div className="tm-card-lg overflow-hidden">
                                <img
                                    src="/images/atelier-loupe.jpg"
                                    alt="Watch movement examined with a loupe"
                                    className="w-full h-[280px] object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <Reveal stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
                        {t.criteria.map((c) => (
                            <div key={c.n} className="group">
                                <div className="flex items-center gap-3">
                                    <div className="tm-serif text-3xl text-[hsl(var(--tm-gold))] transition-transform duration-500 group-hover:-translate-y-1">{c.n}</div>
                                    <div className="h-px flex-1 bg-[hsl(var(--tm-hairline))] transition-colors duration-500 group-hover:bg-[hsl(var(--tm-gold))]"></div>
                                </div>
                                <h3 className="tm-serif text-xl mt-3 text-[hsl(var(--tm-anthracite))]">{c.title}</h3>
                                <p className="mt-4 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed">{c.text}</p>
                            </div>
                        ))}
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <Reveal>
                        <Label>{proc.label}</Label>
                        <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{proc.title}</h2>
                        <p className="mt-5 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed max-w-2xl">{proc.intro}</p>
                    </Reveal>
                    <Reveal stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
                        {proc.steps.map((s) => (
                            <div key={s.n} className="group relative">
                                <div className="tm-serif text-4xl text-[hsl(var(--tm-gold))]">{s.n}</div>
                                <div className="mt-4 h-px w-full bg-[hsl(var(--tm-hairline))] relative overflow-hidden">
                                    <span className="absolute inset-0 bg-[hsl(var(--tm-gold))] -translate-x-full group-hover:translate-x-0 transition-transform duration-700"></span>
                                </div>
                                <h3 className="tm-serif text-lg mt-5 text-[hsl(var(--tm-anthracite))]">{s.title}</h3>
                                <p className="mt-3 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-sm">{s.text}</p>
                            </div>
                        ))}
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                        <Reveal className="lg:col-span-5">
                            <Label>{t.whoLabel}</Label>
                            <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.whoTitle}</h2>
                        </Reveal>
                        <div className="lg:col-span-6 lg:col-start-7">
                            <p className="text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-lg">{t.whoText1}</p>
                            <p className="mt-6 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-lg">{t.whoText2}</p>
                        </div>
                    </div>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <Reveal>
                        <Label>{p.label}</Label>
                        <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{p.title}</h2>
                        <p className="mt-5 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed max-w-2xl">{p.intro}</p>
                    </Reveal>
                    <Reveal stagger className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {p.tiers.map((tier) => (
                            <div
                                key={tier.name}
                                className={tier.featured ? "tm-surface p-10 border-2 border-[hsl(var(--tm-gold))] transition-shadow duration-500 hover:shadow-[0_0_40px_-10px_hsl(var(--tm-gold)/0.45)]" : "tm-card p-10"}
                            >
                                <h3 className="tm-serif text-xl text-[hsl(var(--tm-anthracite))]">{tier.name}</h3>
                                <div className="mt-6 flex items-baseline gap-2">
                                    <span className="tm-serif text-3xl text-[hsl(var(--tm-gold))]">{tier.price}</span>
                                    {tier.unit && <span className="tm-label text-[hsl(var(--tm-muted))]">{tier.unit}</span>}
                                </div>
                                <p className="mt-6 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed text-sm">{tier.desc}</p>
                                <Link
                                    to={tier.name.toLowerCase().includes("préliminaire") || tier.name.toLowerCase().includes("preliminar") || tier.name.toLowerCase().includes("preliminary") ? "/estimation" : "/a-propos"}
                                    className="mt-8 inline-block tm-label tm-gold-link"
                                >
                                    {tier.cta} →
                                </Link>
                            </div>
                        ))}
                    </Reveal>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44">
                    <div className="tm-divider mb-16"></div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                        <Reveal className="lg:col-span-4">
                            <Label>{f.label}</Label>
                            <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{f.title}</h2>
                        </Reveal>
                        <div className="lg:col-span-7 lg:col-start-6">
                            {f.items.map((item, i) => (
                                <FaqItem key={i} question={item.q} answer={item.a} />
                            ))}
                        </div>
                    </div>
                </section>

                <section className="max-w-[1360px] mx-auto px-8 sm:px-16 mt-32 sm:mt-44 mb-8">
                    <Reveal className="tm-surface p-10 sm:p-14">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                            <div className="lg:col-span-8">
                                <Label>{t.ctaLabel}</Label>
                                <h2 className="tm-serif text-3xl sm:text-4xl mt-4 text-[hsl(var(--tm-anthracite))]">{t.ctaTitle}</h2>
                                <p className="mt-5 text-[hsl(var(--tm-anthracite-soft))] leading-relaxed max-w-xl">{t.ctaText}</p>
                            </div>
                            <div className="lg:col-span-4 flex lg:justify-end">
                                <a href="mailto:contact@timemetria.com?subject=Expertise%20request" className="tm-btn-primary">
                                    {t.ctaButton}
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </section>
            </main>
            <Footer />
        </>
    );
}
